import cloneDeep from 'lodash/cloneDeep';

import type { Answer } from '@webapp/account/src/resources/reports/answers';
import { initialAnswer } from '@webapp/account/src/resources/reports/answers';
import type { FilterType } from '@webapp/account/src/resources/reports/filter';
import type { GlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import { initialGlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import type { Report } from '@webapp/account/src/resources/reports/reports';
import type { IWordStat } from '@webapp/account/src/resources/reports/word-stat';

import { REPORT_RESULTS_PAGE_SIZE } from '@webapp/common/lib/const';
import type { SurveyAddonShare } from '@webapp/common/resources/entities/survey-addons/share';
import { initialSurveyAddonShare } from '@webapp/common/resources/entities/survey-addons/share';

export interface NearbyAnswers {
    next: number;
    prev: number;
}

export interface LinkStatResponse {
    filterType: FilterType;
    value: string;
    count: number;
    percent: number;
}

export interface LinkStat {
    id: number;
    totalCount: number;
    uniqueCount: number;
    title: string;
    name: string;
    responses: Array<LinkStatResponse>;
    params: Record<string, any>;
}

export interface ICrossTab {
    params: Record<string, any>;
    id: number;
    survey_id: number;
    title: string;
    after_question: number;
    after_link_param: number;
    questions: Array<{
        id: number;
        linkParamId: number | null;
        questionId: number;
        role: number;
        tabId: number;
        type: number;
    }>;
    order?: number;
}

export interface IFilterGroup {
    dateCreated: string;
    data: string;
    id: number;
    name: string;
}

export interface ComparisonState {
    byPeriod: Record<string, Record<number, Report>>; // periodId -> reportId -> Report
    stale: Set<string>; // `${periodId}:${reportId}`
    fetching: Set<string>; // защита от повторных запросов
    wordStatByPeriod: Record<string, Array<IWordStat>>; // periodId -> wordStat
}

export interface ReportsState {
    answer: Answer;
    answers: Array<Answer>;
    answersPage: number;
    answersTotalCount: number;
    nearbyAnswers: NearbyAnswers;

    reports: Array<Report>;
    staleReports: Set<number>;
    comparison: ComparisonState;

    responsesLimit: number;
    questions: Array<Report>;

    globalFilters: GlobalFilter;
    filtersVisible: boolean;

    share: SurveyAddonShare;
    hideFilters: boolean;

    fetchReports: boolean;
    reportsLoaded: boolean;
    fetchAnswers: boolean;
    fetchPublic: boolean;

    filesSize: number;
    filesCount: number;
    audioSize: number;
    audioCount: number;
    linkStats: Map<number, LinkStat>;
    wordStat: Array<IWordStat>;

    isCrossTabsLoaded: boolean;
    crossTabs: Array<ICrossTab>;
    crossTabsStat: Map<number, any>; // todo
    filterGroups: {
        list: Array<IFilterGroup>;
        surveyId?: number;
    };
    filterGroup?: GlobalFilter;
}

export const reportsInitial: Readonly<ReportsState> = {
    answer: cloneDeep(initialAnswer),
    answers: [],
    answersPage: 1,
    answersTotalCount: 0,
    nearbyAnswers: {
        next: null,
        prev: null,
    },

    reports: [],
    staleReports: new Set([]),
    comparison: {
        byPeriod: {},
        stale: new Set([]),
        fetching: new Set([]),
        wordStatByPeriod: {},
    },
    responsesLimit: REPORT_RESULTS_PAGE_SIZE,
    questions: [],

    // TODO rename to filters, move root filters to .global
    globalFilters: cloneDeep(initialGlobalFilter),
    filtersVisible: false,

    share: cloneDeep(initialSurveyAddonShare),

    fetchReports: true,
    reportsLoaded: false,
    fetchAnswers: false,
    fetchPublic: true,
    hideFilters: false,

    filesCount: 0,
    filesSize: 0,

    audioCount: 0,
    audioSize: 0,
    linkStats: new Map(),
    wordStat: [],

    isCrossTabsLoaded: false,
    crossTabs: [],
    crossTabsStat: new Map(),

    filterGroups: {
        list: [],
        surveyId: null,
    },
};
